import axios from 'axios';
import { store } from '../store/store';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token); // token will be used to update headers inside each retry function
        }
    });
    failedQueue = [];
};

// Attach token to all requests
axiosInstance.interceptors.request.use(
    (config) => {
        const state = store.getState();
        const token = state.auth.auth.token;

        if (token) {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            };
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Handle 401 + refresh token
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(axiosInstance(originalRequest));
                        },
                        reject,
                    });
                });
            }

            isRefreshing = true;

            try {
                const userDetails = JSON.parse(localStorage.getItem('userDetails'));
                const refreshToken = userDetails?.refreshToken;

                if (!refreshToken) throw new Error('No refresh token found');

                const response = await axios.post(
                    `${API_BASE_URL}/auth/refresh`,
                    {},
                    {
                        headers: {
                            'Authorization': `Bearer ${refreshToken}`,
                        },
                    }
                );

                const newToken = response.data?.data?.token;

                if (!newToken) throw new Error('No token in refresh response');

                // Update token in localStorage
                userDetails.token = newToken;
                localStorage.setItem('userDetails', JSON.stringify(userDetails));

                // Update token in Redux
                store.dispatch({
                    type: 'UPDATE_TOKEN',
                    payload: { token: newToken },
                });

                // Process all queued requests with new token
                processQueue(null, newToken);

                // Retry the original request
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);

            } catch (err) {
                processQueue(err, null);
                localStorage.removeItem('userDetails');
                store.dispatch({ type: 'LOGOUT_ACTION' });
                window.location.href = '/login';
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        if (error.response?.status === 412) {
            console.log("Validation failed: " + error.response.data.msg);
            return Promise.resolve(error.response);
        }

        return Promise.reject(error);
    }
);

// Optional: Offline alert
window.addEventListener("offline", () => {
    alert("You are offline. Please check your internet connection.");
});

export default axiosInstance;
